/**
 * Thin API client for the Pages SPA. All calls go through the same-origin
 * `/api/*` proxy (functions/api/[[...path]].ts), which verifies the session
 * cookie and forwards to the Worker with the X-Tenant-Session header injected.
 *
 * No base URL needed — same origin. No auth headers needed — the cookie is
 * sent automatically by the browser and verified by the proxy.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  path: string,
  init?: RequestInit
): Promise<T> {
  // Strip a leading slash so callers can pass 'onboarding/status' or
  // '/onboarding/status' interchangeably.
  const cleanPath = path.replace(/^\//, '');
  const res = await fetch(`/api/${cleanPath}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  // Try to parse JSON regardless of status — the Worker always returns JSON.
  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!res.ok) {
    const message =
      (typeof body === 'object' && body !== null && 'error' in body
        ? String((body as { error: unknown }).error)
        : null) ?? `Request failed (${res.status})`;
    throw new ApiError(res.status, message, body);
  }

  return body as T;
}

export const api = {
  get: <T>(path: string, init?: RequestInit) => request<T>(path, init),
  post: <T>(path: string, payload?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: payload === undefined ? undefined : JSON.stringify(payload),
    }),
};
